import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, Avatar, Chip, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Skeleton,
  LinearProgress, useTheme, alpha, ToggleButtonGroup, ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  EmojiEvents, MilitaryTech, StarBorder, SportsBaseball,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { awardsApi } from '../services/apiService';
import { getTeamLogoUrl } from '../utils/teamLogos';
import { makePlayerSlug } from '../utils/playerSlug';
import { useYear } from '../context/YearContext';
import PageYearSelector from '../components/common/PageYearSelector';
import CountUp from '../components/common/CountUp';

/* ---------- Award display configs ---------- */
const fmt = {
  d1: v => (v == null ? '—' : Number(v).toFixed(1)),
  d2: v => (v == null ? '—' : Number(v).toFixed(2)),
  d3: v => (v == null ? '—' : Number(v).toFixed(3).replace(/^0/, '')),
  int: v => (v == null ? '—' : Math.round(Number(v))),
};

const AWARD_CONFIGS = [
  {
    key: 'mvp',
    label: 'MVP',
    icon: <EmojiEvents sx={{ fontSize: 18 }} />,
    gradient: 'linear-gradient(135deg, #FFD700, #FFA000)',
    columns: [
      { key: 'war', label: 'WAR', fmt: fmt.d1, width: 60, primary: true },
      { key: 'ops', label: 'OPS', fmt: fmt.d3, width: 60, primary: true },
      { key: 'hr', label: 'HR', fmt: fmt.int, width: 50 },
      { key: 'rbi', label: 'RBI', fmt: fmt.int, width: 50 },
      { key: 'avg', label: 'AVG', fmt: fmt.d3, width: 55 },
      { key: 'sb', label: 'SB', fmt: fmt.int, width: 45 },
    ],
  },
  {
    key: 'cyYoung',
    label: 'Cy Young',
    icon: <SportsBaseball sx={{ fontSize: 18 }} />,
    gradient: 'linear-gradient(135deg, #1565C0, #42A5F5)',
    columns: [
      { key: 'war', label: 'WAR', fmt: fmt.d1, width: 60, primary: true },
      { key: 'era', label: 'ERA', fmt: fmt.d2, width: 55, primary: true },
      { key: 'k', label: 'K', fmt: fmt.int, width: 50 },
      { key: 'ip', label: 'IP', fmt: fmt.d1, width: 55 },
      { key: 'whip', label: 'WHIP', fmt: fmt.d2, width: 60 },
      { key: 'fip', label: 'FIP', fmt: fmt.d2, width: 55 },
    ],
  },
  {
    key: 'roy',
    label: 'ROY',
    icon: <StarBorder sx={{ fontSize: 18 }} />,
    gradient: 'linear-gradient(135deg, #2E7D32, #66BB6A)',
    fullWidth: true,
    columns: [
      { key: 'war', label: 'WAR', fmt: fmt.d1, width: 60, primary: true },
      { key: 'ops', label: 'OPS', fmt: fmt.d3, width: 60, primary: true },
      { key: 'hr', label: 'HR', fmt: fmt.int, width: 50 },
      { key: 'avg', label: 'AVG', fmt: fmt.d3, width: 55 },
      { key: 'era', label: 'ERA', fmt: fmt.d2, width: 55 },
    ],
  },
];

const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32']; // gold, silver, bronze
const EMPTY = { AL: { mvp: [], cyYoung: [], roy: [] }, NL: { mvp: [], cyYoung: [], roy: [] } };

const AwardsTracker = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { year: globalYear } = useYear();
  const effectiveYear = globalYear || new Date().getFullYear();

  const [league, setLeague] = useState('AL');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(EMPTY);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await awardsApi.getAwards({ year: effectiveYear, limit: 15 });
      setData(res?.awards || EMPTY);
    } catch (err) {
      console.error('Awards load error:', err);
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [effectiveYear]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 2 }, py: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MilitaryTech sx={{ fontSize: 28, color: 'warning.main' }} />
          <Typography variant="h5" fontWeight={800}>Awards Tracker</Typography>
          <Chip size="small" label={effectiveYear} sx={{ fontWeight: 700, ml: 0.5 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={league}
            exclusive
            onChange={(_, v) => v && setLeague(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 2, py: 0.3, fontWeight: 700, fontSize: '0.8rem' } }}
          >
            <ToggleButton value="AL">AL</ToggleButton>
            <ToggleButton value="NL">NL</ToggleButton>
          </ToggleButtonGroup>
          <PageYearSelector />
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2}>
        {AWARD_CONFIGS.map(ac => {
          const candidates = data?.[league]?.[ac.key] || [];
          const topWar = candidates[0]?.war || 1;

          return (
            <Grid item xs={12} md={ac.fullWidth ? 12 : 6} key={ac.key}>
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', height: '100%' }}>
                {/* Award Header */}
                <Box sx={{
                  background: ac.gradient,
                  color: 'white', py: 1, px: 2,
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  {ac.icon}
                  <Typography variant="subtitle1" fontWeight={800}>
                    {league} {ac.label}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    size="small"
                    label={`${candidates.length} candidates`}
                    sx={{ height: 20, bgcolor: alpha('#fff', 0.25), color: 'white', fontWeight: 600, fontSize: '0.68rem' }}
                  />
                </Box>

                {/* Leader Podium — top 3 */}
                {candidates.length >= 3 && (
                  <Box sx={{ display: 'flex', gap: 1, p: 1.5, pb: 0.5, justifyContent: 'center' }}>
                    {candidates.slice(0, 3).map((c, i) => (
                      <Box
                        key={i}
                        sx={{
                          textAlign: 'center', cursor: 'pointer', flex: 1, maxWidth: 120,
                          p: 1, borderRadius: 2,
                          bgcolor: alpha(RANK_COLORS[i], 0.08),
                          border: `2px solid ${alpha(RANK_COLORS[i], 0.3)}`,
                          transition: 'all 0.15s',
                          '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 },
                        }}
                        onClick={() => navigate(`/players/profile/${makePlayerSlug(c.name, c.mlbId)}`)}
                      >
                        <Avatar src={getTeamLogoUrl(c.team)} sx={{ width: 32, height: 32, mx: 'auto', mb: 0.5 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                        <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem', display: 'block', lineHeight: 1.2 }}>
                          {c.name?.split(' ').pop() || c.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {c.team} · {c.position}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="h6" fontWeight={800} fontFamily="monospace" sx={{ fontSize: '1rem', color: i === 0 ? 'warning.main' : i === 1 ? 'text.secondary' : '#8B4513' }}>
                            <CountUp endVal={c.war || 0} decimals={1} duration={1200} />
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>WAR</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Full Standings Table */}
                <TableContainer sx={{ maxHeight: ac.fullWidth ? 350 : 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 30, fontSize: '0.7rem', py: 0.5 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 0.5 }}>Player</TableCell>
                        {ac.columns.map(col => (
                          <TableCell key={col.key} align="right" sx={{
                            fontWeight: 700, width: col.width, fontSize: '0.7rem', py: 0.5,
                            display: { xs: col.primary ? 'table-cell' : 'none', sm: 'table-cell' },
                          }}>
                            {col.label}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 700, width: 60, fontSize: '0.7rem', py: 0.5, display: { xs: 'none', sm: 'table-cell' } }}>
                          Share
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        Array.from({ length: 5 }).map((_, i) => (
                          <TableRow key={i}><TableCell colSpan={8}><Skeleton variant="text" /></TableCell></TableRow>
                        ))
                      ) : candidates.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">No data available</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        candidates.map((c, idx) => {
                          const warPct = topWar > 0 ? ((c.war || 0) / topWar) * 100 : 0;
                          return (
                            <TableRow
                              key={idx}
                              hover
                              sx={{
                                cursor: 'pointer',
                                bgcolor: idx < 3 ? alpha(RANK_COLORS[idx] || theme.palette.primary.main, 0.03) : undefined,
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                              }}
                              onClick={() => navigate(`/players/profile/${makePlayerSlug(c.name, c.mlbId)}`)}
                            >
                              <TableCell sx={{ py: 0.4 }}>
                                <Chip
                                  size="small"
                                  label={c.rank || idx + 1}
                                  sx={{
                                    height: 20, width: 24, fontSize: '0.7rem', fontWeight: 700,
                                    bgcolor: idx < 3 ? alpha(RANK_COLORS[idx], 0.15) : alpha(theme.palette.text.secondary, 0.08),
                                    color: idx < 3 ? (idx === 0 ? 'warning.dark' : idx === 1 ? 'text.secondary' : '#8B4513') : 'text.secondary',
                                    '& .MuiChip-label': { px: 0 },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.4 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Avatar src={getTeamLogoUrl(c.team)} sx={{ width: 20, height: 20 }} imgProps={{ style: { objectFit: 'contain', background: 'white' } }} />
                                  <Box>
                                    <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.78rem', display: 'block', lineHeight: 1.2 }}>
                                      {c.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                      {c.team} · {c.position}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              {ac.columns.map(col => (
                                <TableCell key={col.key} align="right" sx={{
                                  py: 0.4,
                                  fontFamily: 'monospace', fontWeight: col.key === 'war' ? 700 : 500,
                                  fontSize: '0.78rem',
                                  color: col.key === 'war' && idx < 3 ? 'primary.main' : undefined,
                                  display: { xs: col.primary ? 'table-cell' : 'none', sm: 'table-cell' },
                                }}>
                                  {col.fmt(c.stats?.[col.key])}
                                </TableCell>
                              ))}
                              <TableCell align="right" sx={{ py: 0.4, display: { xs: 'none', sm: 'table-cell' } }}>
                                <Tooltip title={`${warPct.toFixed(0)}% of leader`}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(warPct, 100)}
                                    sx={{
                                      height: 6, borderRadius: 3, width: 50,
                                      bgcolor: alpha(theme.palette.text.secondary, 0.08),
                                      '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        bgcolor: idx === 0 ? 'warning.main' : idx < 3 ? 'primary.main' : 'text.secondary',
                                      },
                                    }}
                                  />
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2, fontSize: '0.7rem' }}>
        Rankings based on season WAR. ROY candidates filtered by service-time data (&lt;1 year MLB service).
        Data updates nightly.
      </Typography>
    </Box>
  );
};

export default AwardsTracker;
